import { z } from 'zod';
import { command, get, post, put } from '../http';
import { type ListQuery, paginated, resource } from '../schemas/common';
import { kycRequestSchema, userSchema } from '../schemas/domain';

// wallet_address is intentionally NOT here — 2026-07-31 security fix. It's
// the member's sole login credential now; changing it must go through
// proof-of-ownership (registration) or an admin's audited action, never a
// plain profile edit. See ProfileController::update()'s docblock.
export const profileInput = z.object({
  name: z.string().min(2),
  phone: z.string().min(6),
});
export type ProfileInput = z.infer<typeof profileInput>;

export const changePasswordInput = z
  .object({
    current_password: z.string().min(1),
    password: z.string().min(8),
    password_confirmation: z.string(),
  })
  .refine((v) => v.password === v.password_confirmation, {
    message: 'Passwords do not match',
    path: ['password_confirmation'],
  });
export type ChangePasswordInput = z.infer<typeof changePasswordInput>;

/**
 * Digits 4-8, matching RegisterRequest's `transaction_pin` rule
 * (`digits_between:4,8`). `current_pin` / `current_password` are both
 * optional client-side — the server requires at least one to verify identity,
 * whichever the member has set / remembers.
 */
export const changeTransactionPinInput = z
  .object({
    current_pin: z.string().regex(/^\d*$/, 'PIN must be numeric').optional(),
    current_password: z.string().optional(),
    pin: z.string().regex(/^\d{4,8}$/, 'PIN must be 4-8 digits'),
    pin_confirmation: z.string(),
  })
  .refine((v) => v.pin === v.pin_confirmation, {
    message: 'PINs do not match',
    path: ['pin_confirmation'],
  })
  .refine((v) => Boolean(v.current_pin) || Boolean(v.current_password), {
    message: 'Enter your current PIN or your login password',
    path: ['current_password'],
  });
export type ChangeTransactionPinInput = z.infer<typeof changeTransactionPinInput>;

export const kycInput = z.object({
  document_type: z.string().min(2),
  document_number: z.string().min(4),
});
export type KycInput = z.infer<typeof kycInput>;

/**
 * `status` is inherited from ListQuery as a plain string: these queries are
 * built from URL search params, so narrowing to UserStatus here would only
 * force casts at every call site. The server validates it.
 */
export type MemberListQuery = ListQuery;

/**
 * Admin edit of a member's basic profile fields. Deliberately no uniqueness
 * check on email/phone client-side either — the server allows duplicates
 * across members (2026-07-27 business decision, see RegisterRequest).
 *
 * wallet_address is the 2026-07-31 hard-cutover escape hatch — lets an admin
 * attach a wallet to a pre-existing (pre-wallet-auth) account so its owner
 * can log in again now that email+password is disabled. Optional: an empty
 * string means "leave untouched" (see MemberDetail.tsx's submit handler,
 * which strips '' to undefined before POSTing), matching the backend's
 * `$request->filled('wallet_address')` gate in MemberController::update() —
 * a present-but-empty value is never sent, so it can never blank out an
 * existing address.
 */
export const memberUpdateInput = z.object({
  name: z.string().min(2, 'Name is required').max(120),
  email: z.string().email('Enter a valid email'),
  phone: z.string().max(32).nullable().optional(),
  wallet_address: z
    .string()
    .regex(/^0x[a-fA-F0-9]{40}$/, 'Enter a valid wallet address (0x + 40 hex characters)')
    .optional()
    .or(z.literal(''))
    .transform((v) => (v === '' ? undefined : v)),
});
export type MemberUpdateInput = z.infer<typeof memberUpdateInput>;

export const usersApi = {
  /* ---- self-service (user panel) ---- */
  profile: () => get('/profile', resource(userSchema)),
  updateProfile: (input: ProfileInput) => put('/profile', resource(userSchema), input),
  changePassword: (input: ChangePasswordInput) => command('/profile/password', input),
  changeTransactionPin: (input: ChangeTransactionPinInput) => command('/profile/transaction-pin', input),
  enableTwoFactor: () =>
    post('/profile/two-factor/enable', resource(z.object({ secret: z.string(), qr_svg: z.string() }))),
  confirmTwoFactor: (code: string) => command('/profile/two-factor/confirm', { code }),
  disableTwoFactor: (code: string) => command('/profile/two-factor/disable', { code }),

  submitKyc: (form: FormData) => post('/profile/kyc', resource(kycRequestSchema), form),
  kycStatus: () => get('/profile/kyc', resource(kycRequestSchema.nullable())),

  /* ---- admin ---- */
  list: (query: MemberListQuery = {}) => get('/admin/members', paginated(userSchema), query),
  show: (id: string) => get(`/admin/members/${id}`, resource(userSchema)),
  search: (q: string) => get('/admin/members/search', paginated(userSchema), { q }),
  update: (id: string, input: MemberUpdateInput) =>
    post(`/admin/members/${id}/update`, resource(userSchema), input),
};
